import { get3DWeightEstimationResult } from "@/api/aiProduct";
import {
  addCartItem,
  getCartItemList,
  getCartList,
  removeCartItem,
  updateCartItemQuantity,
} from "@/api/cart";
import type {
  CartItem,
  CartItemWithInfo,
  CartListItem,
  CartSelection,
  CartState,
  CartTotals,
  OrderCartSelections,
  OrderGroup,
} from "@/types/cart";
import { ProductType } from "@/types/product";
import { add, calcItemTotal } from "@/utils/common";
import { defineStore } from "pinia";
import { ElMessage } from "element-plus";

// ============================================
// 结算缓存相关
// ============================================

/** 结算快照数据缓存结构（存储完整商品数据，不受购物车变化影响） */
interface CheckoutSnapshotCache {
  /** 订单组 ID */
  orderId: number;
  /** 订单组名称 */
  orderName: string;
  /** 选中商品的完整数据快照 */
  items: CartItem[];
  /** 缓存时间戳 */
  timestamp: number;
}

/** 获取结算缓存的 localStorage key */
function getCheckoutCacheKey(orderId: number): string {
  return `checkout_snapshot_${orderId}`;
}

// ============================================
// 数据转换工具函数
// ============================================

/**
 * 将后端购物车商品数据转换为前端格式
 */
function transformCartItem(item: CartItemWithInfo): CartItem {
  const { product_info } = item;
  return {
    cartItemId: item.shopping_cart_item_id,
    productSpu: item.product_spu,
    productSku: item.product_sku,
    productId: product_info?.product_id || 0,
    productTitle: product_info?.product_name || "",
    price: product_info?.unit_price || 0,
    comparePrice: product_info?.compare_unit_price || 0,
    quantity: item.quantity,
    image:
      item.display_url ||
      product_info?.display_url ||
      product_info?.product_image ||
      "",
    stock: product_info?.stock || 0,
    skuInfo: product_info?.sku_info || [],
    isAvailable: product_info?.is_available ?? true,
    isInvalid: product_info?.is_invalid ?? false,
    invalidReason: product_info?.invalid_reason || "",
    productType:
      product_info?.product_type === ProductType.AI
        ? ProductType.AI
        : ProductType.Physical,
    logisticsDeliveryId: product_info?.logistics_delivery_id || 0,
    creationId: item.creation_id || undefined,
    extra: item.extra,
    estimatedPrice:
      item.estimated_price != null && item.estimated_price !== 0
        ? item.estimated_price
        : undefined,
    weightEstimationRecordId: item.weight_estimation_record_id || undefined,
  };
}

/** 使用报价记录恢复 3D 手办优化信息，避免依赖加购前的内存状态。 */
async function hydrateFigurineQuotes(items: CartItem[]) {
  await Promise.all(
    items.map(async (item) => {
      if (!item.weightEstimationRecordId || item.print3DInfo) return;
      try {
        const response = await get3DWeightEstimationResult(
          item.weightEstimationRecordId,
        );
        const quote = response.data;
        if (!quote || quote.status !== 2) return;
        if (quote.estimated_price != null) {
          item.estimatedPrice = Number(quote.estimated_price);
        }
        item.modelOptimizeName = quote.model_optimize_name || undefined;
        item.modelOptimizeAmount = Number(quote.model_optimize_amount || 0);
      } catch {
        // 报价恢复失败时保留购物车基础报价，不阻断购物车加载。
      }
    }),
  );
}

/**
 * 将后端购物车列表数据转换为前端 CartState 格式
 */
function transformCartList(list: CartListItem[]): CartState {
  const state: CartState = {};

  // 初始化 6 个订单组
  for (let i = 1; i <= 6; i++) {
    state[i] = { id: i, name: `订单${i}`, items: [] };
  }

  // 填充后端数据
  list.forEach((cart) => {
    const index = cart.cart_index;
    if (index >= 1 && index <= 6) {
      state[index] = {
        id: index,
        name: cart.name || `订单${index}`,
        items: (cart.items || []).map(transformCartItem),
      };
    }
  });

  return state;
}

/**
 * 获取默认购物车状态（空的 6 个订单组）
 */
function getDefaultCartState(): CartState {
  return {
    1: { id: 1, name: "订单1", items: [] },
    2: { id: 2, name: "订单2", items: [] },
    3: { id: 3, name: "订单3", items: [] },
    4: { id: 4, name: "订单4", items: [] },
    5: { id: 5, name: "订单5", items: [] },
    6: { id: 6, name: "订单6", items: [] },
  };
}

// ============================================
// 常量定义
// ============================================

/** 单订单组最大商品数量限制 */
const MAX_ITEMS_PER_ORDER = 100;

// ============================================
// 购物车 Store
// ============================================

/**
 * 购物车状态管理
 * B2B 特有：支持6个订单组，实现一键分销到多个订单
 */
export const useCartStore = defineStore("cart", () => {
  // ============ State ============

  /** 购物车状态（6个订单组） */
  const cart = ref<CartState>(getDefaultCartState());

  /** 当前活动的订单组 ID */
  const activeOrderId = ref<number>(1);

  /** 当前订单组内选中的商品（cartItemId -> boolean） */
  const selectedItems = ref<Record<string, boolean>>({});

  /** 加载状态 */
  const loading = ref(false);

  /** 是否已初始化 */
  const initialized = ref(false);

  // ============ Getters ============

  /** 当前活动的订单组 */
  const currentOrder = computed(() => cart.value[activeOrderId.value]);

  /** 购物车总商品种类数（排除失效商品） */
  const totalCartCount = computed(() => {
    return Object.values(cart.value).reduce<number>(
      (sum, group) =>
        sum +
        group.items.filter(
          (item: CartItem) => !item.isInvalid && item.isAvailable,
        ).length,
      0,
    );
  });

  /** 获取指定订单组的商品总数量 */
  function getOrderTotalItems(orderId: number): number {
    return (
      cart.value[orderId]?.items.reduce(
        (sum, item) => sum + item.quantity,
        0,
      ) || 0
    );
  }

  /** 当前订单组可选商品（排除失效商品） */
  const selectableItems = computed(() => {
    return currentOrder.value.items.filter(
      (item) => !item.isInvalid && item.isAvailable,
    );
  });

  /** 当前订单组是否全选（只考虑可选商品） */
  const isAllSelected = computed(() => {
    const items = selectableItems.value;
    return (
      items.length > 0 &&
      items.every((item) => selectedItems.value[item.cartItemId])
    );
  });

  /** 当前订单选中商品的汇总（使用精确计算避免浮点数精度丢失，排除失效商品） */
  const selectedTotals = computed<CartTotals>(() => {
    let count = 0;
    let price = 0;
    currentOrder.value.items.forEach((item) => {
      // 只计算选中且有效的商品
      if (
        selectedItems.value[item.cartItemId] &&
        !item.isInvalid &&
        item.isAvailable
      ) {
        count += item.quantity;
        price = add(
          price,
          add(
            calcItemTotal(item.estimatedPrice ?? item.price, item.quantity),
            item.modelOptimizeAmount || 0,
          ),
        );
      }
    });
    return { count, price };
  });

  // ============ Actions ============

  /**
   * 全选指定订单组的所有商品（排除失效商品）
   * @param orderId 订单组 ID
   */
  function selectAllForOrder(orderId: number) {
    const order = cart.value[orderId];
    if (!order) return;

    // 先清空当前选中状态，再全选指定订单组（只选有效商品）
    selectedItems.value = {};
    order.items.forEach((item) => {
      if (!item.isInvalid && item.isAvailable) {
        selectedItems.value[item.cartItemId] = true;
      }
    });
  }

  /**
   * 初始化购物车（从后端获取数据）
   */
  async function initCart() {
    if (loading.value) return;

    loading.value = true;
    try {
      const response = await getCartList();
      if (response.data?.list) {
        const nextCart = transformCartList(response.data.list);
        await hydrateFigurineQuotes(
          Object.values(nextCart).flatMap((group) => group.items),
        );
        cart.value = nextCart;
      }
      initialized.value = true;

      // 在同一个更新周期内设置选中状态，避免闪烁
      const currentOrderItems = cart.value[activeOrderId.value]?.items || [];
      const newSelection: Record<string, boolean> = {};
      currentOrderItems.forEach((item) => {
        if (!item.isInvalid && item.isAvailable) {
          newSelection[item.cartItemId] = true;
        }
      });
      selectedItems.value = newSelection;
    } catch (error) {
      console.error("获取购物车失败:", error);
      // 失败时使用默认空状态
      cart.value = getDefaultCartState();
    } finally {
      loading.value = false;
    }
  }

  /**
   * 刷新购物车数据
   */
  async function refreshCart() {
    initialized.value = false;
    await initCart();
  }

  /**
   * 检查订单组是否会超过商品数量限制
   * @param orderId 订单组 ID
   * @param newItemsCount 新增商品数量
   * @returns 是否会超限
   */
  function checkOrderLimit(orderId: number, newItemsCount: number): boolean {
    const currentCount = cart.value[orderId]?.items.length || 0;
    return currentCount + newItemsCount > MAX_ITEMS_PER_ORDER;
  }

  /**
   * 获取订单组剩余可添加商品数量
   * @param orderId 订单组 ID
   * @returns 剩余可添加数量
   */
  function getOrderRemainingSlots(orderId: number): number {
    const currentCount = cart.value[orderId]?.items.length || 0;
    return Math.max(0, MAX_ITEMS_PER_ORDER - currentCount);
  }

  /**
   * 添加商品到购物车
   * @param orderIds 目标订单组 ID 列表
   * @param selections SKU 选择列表（SKU + 数量）
   * @throws 当任一订单组超过100商品限制时抛出错误
   */
  async function addToCart(orderIds: number[], selections: CartSelection[]) {
    if (selections.length === 0 || orderIds.length === 0) return;

    // 构建商品列表
    const products = selections
      .filter((sel) => sel.qty > 0)
      .map((sel) => ({
        product_sku: sel.productSku,
        quantity: sel.qty,
      }));

    if (products.length === 0) return;

    // 检查每个目标订单组是否会超过100商品限制
    const exceedingOrders: {
      orderId: number;
      current: number;
      adding: number;
    }[] = [];

    for (const orderId of orderIds) {
      const order = cart.value[orderId];
      if (!order) continue;

      // 计算真正新增的商品数量（排除已存在的SKU）
      const existingSkus = new Set(order.items.map((item) => item.productSku));
      const newSkuCount = products.filter(
        (item) => !existingSkus.has(item.product_sku),
      ).length;

      if (
        newSkuCount > 0 &&
        order.items.length + newSkuCount > MAX_ITEMS_PER_ORDER
      ) {
        exceedingOrders.push({
          orderId,
          current: order.items.length,
          adding: newSkuCount,
        });
      }
    }

    // 如果有订单组会超限，抛出错误
    if (exceedingOrders.length > 0) {
      const orderNames = exceedingOrders
        .map((o) => `订单${o.orderId}`)
        .join("、");
      const error = new Error(
        `${orderNames}已达到商品数量上限（最多${MAX_ITEMS_PER_ORDER}个商品），请移除部分商品后再添加`,
      );
      (
        error as Error & { code: string; details: typeof exceedingOrders }
      ).code = "ORDER_LIMIT_EXCEEDED";
      (error as Error & { details: typeof exceedingOrders }).details =
        exceedingOrders;
      throw error;
    }

    try {
      // 构建新的请求结构：每个购物车索引对应自己的商品列表
      const response = await addCartItem({
        items: orderIds.map((cartIndex) => ({
          cart_index: cartIndex,
          products,
        })),
      });

      // 检查是否有失败的项
      const failedResults =
        response.data?.results?.filter((r) => r.error_msg) || [];
      if (failedResults.length > 0) {
        console.warn("部分商品添加失败:", failedResults);
      }

      // 刷新购物车数据
      await refreshCart();

      return response.data;
    } catch (error) {
      console.error("添加购物车失败:", error);
      throw error;
    }
  }

  /**
   * 按订单清单添加商品到购物车
   * @description 每个订单可以有独立的商品列表，一次请求添加到多个购物车
   * @param orderSelections 订单清单数组，每项包含订单ID和对应的SKU选择列表
   * @throws 当任一订单组超过100商品限制时抛出错误
   */
  async function addToCartByOrders(orderSelections: OrderCartSelections[]) {
    if (orderSelections.length === 0) return;

    // 过滤掉空的订单
    const validOrders = orderSelections.filter(
      (order) =>
        order.selections.length > 0 &&
        order.selections.some((sel) => sel.qty > 0),
    );
    if (validOrders.length === 0) return;

    // 检查每个目标订单组是否会超过100商品限制
    const exceedingOrders: {
      orderId: number;
      current: number;
      adding: number;
    }[] = [];

    for (const orderSel of validOrders) {
      const order = cart.value[orderSel.orderId];
      if (!order) continue;

      const products = orderSel.selections.filter((sel) => sel.qty > 0);
      const existingSkus = new Set(order.items.map((item) => item.productSku));
      const newSkuCount = products.filter(
        (sel) => !existingSkus.has(sel.productSku),
      ).length;

      if (
        newSkuCount > 0 &&
        order.items.length + newSkuCount > MAX_ITEMS_PER_ORDER
      ) {
        exceedingOrders.push({
          orderId: orderSel.orderId,
          current: order.items.length,
          adding: newSkuCount,
        });
      }
    }

    // 如果有订单组会超限，抛出错误
    if (exceedingOrders.length > 0) {
      const orderNames = exceedingOrders
        .map((o) => `订单${o.orderId}`)
        .join("、");
      const error = new Error(
        `${orderNames}已达到商品数量上限（最多${MAX_ITEMS_PER_ORDER}个商品），请移除部分商品后再添加`,
      );
      (
        error as Error & { code: string; details: typeof exceedingOrders }
      ).code = "ORDER_LIMIT_EXCEEDED";
      (error as Error & { details: typeof exceedingOrders }).details =
        exceedingOrders;
      throw error;
    }

    try {
      // 构建请求结构：每个购物车索引对应自己独立的商品列表
      const response = await addCartItem({
        items: validOrders.map((orderSel) => ({
          cart_index: orderSel.orderId,
          products: orderSel.selections
            .filter((sel) => sel.qty > 0)
            .map((sel) => ({
              product_sku: sel.productSku,
              quantity: sel.qty,
            })),
        })),
      });

      const totalResultsLength = response.data?.results?.length || 0;

      // 检查是否有失败的项
      const failedResultsLength =
        response.data?.results?.filter((r) => !r.success).length || 0;

      const successResultsLength = totalResultsLength - failedResultsLength;

      if (successResultsLength === totalResultsLength) {
        ElMessage.success(`${successResultsLength}件商品成功加入采购车`);
      } else if (
        failedResultsLength > 0 &&
        failedResultsLength < totalResultsLength
      ) {
        ElMessage.warning(
          `${successResultsLength}件商品成功加入采购车，${failedResultsLength}件商品加入失败`,
        );
      } else {
        ElMessage.error(`${totalResultsLength}件商品加入失败`);
      }

      // 刷新购物车数据
      await refreshCart();

      return response.data;
    } catch (error) {
      console.error("添加购物车失败:", error);
      throw error;
    }
  }

  /**
   * 更新购物车商品数量
   * @param orderId 订单组 ID
   * @param cartItemId 购物车商品ID
   * @param delta 数量变化值（正数增加，负数减少）
   */
  async function updateQty(orderId: number, cartItemId: string, delta: number) {
    const items = cart.value[orderId].items;
    const item = items.find((i) => i.cartItemId === cartItemId);
    if (!item || item.isInvalid || !item.isAvailable) return;

    const newQty = Math.max(1, item.quantity + delta);

    // 乐观更新
    const oldQty = item.quantity;
    item.quantity = newQty;

    try {
      await updateCartItemQuantity({
        shopping_cart_item_id: cartItemId,
        quantity: newQty,
      });
    } catch (error) {
      // 回滚
      item.quantity = oldQty;
      console.error("更新数量失败:", error);
      const message = (error as ApiError).message || "更新数量失败";
      ElMessage.error(message);
      throw error;
    }
  }

  /**
   * 直接设置购物车商品数量
   * @param orderId 订单组 ID
   * @param cartItemId 购物车商品ID
   * @param quantity 目标数量
   */
  async function setQty(orderId: number, cartItemId: string, quantity: number) {
    const items = cart.value[orderId].items;
    const item = items.find((i) => i.cartItemId === cartItemId);
    if (!item || item.isInvalid || !item.isAvailable) return;

    const newQty = Math.max(1, quantity);

    // 乐观更新
    const oldQty = item.quantity;
    item.quantity = newQty;

    try {
      await updateCartItemQuantity({
        shopping_cart_item_id: cartItemId,
        quantity: newQty,
      });
    } catch (error) {
      // 回滚
      item.quantity = oldQty;
      console.error("设置数量失败:", error);
      throw error;
    }
  }

  /**
   * 从购物车移除商品
   * @param orderId 订单组 ID
   * @param cartItemId 购物车商品ID
   */
  async function removeItem(orderId: number, cartItemId: string) {
    const items = cart.value[orderId].items;
    const itemIndex = items.findIndex((i) => i.cartItemId === cartItemId);
    if (itemIndex === -1) return;

    // 乐观更新
    const removedItem = items.splice(itemIndex, 1)[0];
    delete selectedItems.value[cartItemId];

    try {
      await removeCartItem({
        shopping_cart_item_id: cartItemId,
      });
    } catch (error) {
      // 回滚
      items.splice(itemIndex, 0, removedItem);
      console.error("删除商品失败:", error);
      throw error;
    }
  }

  /**
   * 切换商品选中状态
   * @param cartItemId 购物车商品ID
   */
  function toggleItemSelection(cartItemId: string) {
    selectedItems.value[cartItemId] = !selectedItems.value[cartItemId];
  }

  /**
   * 切换全选/取消全选（只操作可选商品，排除失效商品）
   */
  function toggleAll() {
    const shouldSelect = !isAllSelected.value;
    currentOrder.value.items.forEach((item) => {
      // 只操作有效商品
      if (!item.isInvalid && item.isAvailable) {
        selectedItems.value[item.cartItemId] = shouldSelect;
      }
    });
  }

  /**
   * 获取指定订单组的商品列表
   * @param orderId 订单组 ID
   * @param autoSelect 是否自动全选有效商品，默认 true
   */
  /**
   * 获取指定订单组的商品列表（仅刷新数据，不切换订单）
   * @param orderId 订单组 ID
   */
  async function fetchOrderItems(orderId: number) {
    if (loading.value) return;

    loading.value = true;
    try {
      const response = await getCartItemList({ cart_index: orderId });
      if (response.data?.list) {
        const items = response.data.list.map(transformCartItem);
        await hydrateFigurineQuotes(items);
        const currentOrder = cart.value[orderId];
        cart.value[orderId] = {
          ...currentOrder,
          items,
        } as OrderGroup;

        // 如果是当前活动订单，同步更新选中状态
        if (orderId === activeOrderId.value) {
          const newSelection: Record<string, boolean> = {};
          items.forEach((item) => {
            if (!item.isInvalid && item.isAvailable) {
              newSelection[item.cartItemId] = true;
            }
          });
          selectedItems.value = newSelection;
        }
      }
    } catch (error) {
      console.error(`获取订单${orderId}商品列表失败:`, error);
    } finally {
      loading.value = false;
    }
  }

  /**
   * 切换活动订单组
   * @param orderId 订单组 ID
   * @param needFetch 是否需要获取数据，默认 true
   */
  async function setActiveOrder(orderId: number, needFetch: boolean = true) {
    if (needFetch) {
      // 先获取数据，数据准备好后再切换订单组
      // fetchOrderItems 内部会设置选中状态，然后再切换 activeOrderId
      // 这样视图更新时数据和选中状态都已就绪
      await fetchOrderItemsAndSwitch(orderId);
    } else {
      // 不需要获取数据时，直接切换并全选
      activeOrderId.value = orderId;
      selectAllForOrder(orderId);
    }
  }

  /**
   * 获取订单商品并切换到该订单（内部方法）
   * @description 确保数据和选中状态在切换订单前都已准备好，避免闪烁
   */
  async function fetchOrderItemsAndSwitch(orderId: number) {
    if (loading.value) return;

    loading.value = true;
    try {
      const response = await getCartItemList({ cart_index: orderId });
      if (response.data?.list) {
        const items = response.data.list.map(transformCartItem);
        await hydrateFigurineQuotes(items);
        const currentOrder = cart.value[orderId];

        // 准备选中状态
        const newSelection: Record<string, boolean> = {};
        items.forEach((item) => {
          if (!item.isInvalid && item.isAvailable) {
            newSelection[item.cartItemId] = true;
          }
        });

        // 按顺序更新：选中状态 → 商品数据 → 活动订单ID
        // 这样当视图因 activeOrderId 变化而更新时，其他数据都已就绪
        selectedItems.value = newSelection;
        cart.value[orderId] = {
          ...currentOrder,
          items,
        } as OrderGroup;
        activeOrderId.value = orderId;
      }
    } catch (error) {
      console.error(`获取订单${orderId}商品列表失败:`, error);
      // 即使失败也要切换订单组，避免 UI 卡住
      activeOrderId.value = orderId;
    } finally {
      loading.value = false;
    }
  }

  /**
   * 获取选中的商品列表（只返回有效商品）
   */
  function getSelectedItems(): CartItem[] {
    return currentOrder.value.items.filter(
      (item) =>
        selectedItems.value[item.cartItemId] &&
        !item.isInvalid &&
        item.isAvailable,
    );
  }

  /**
   * 全选当前订单组的所有商品（排除失效商品）
   */
  function selectAllCurrentOrder() {
    const newSelection: Record<string, boolean> = {};
    currentOrder.value.items.forEach((item) => {
      // 只选有效商品
      if (!item.isInvalid && item.isAvailable) {
        newSelection[item.cartItemId] = true;
      }
    });
    selectedItems.value = newSelection;
  }

  /**
   * 清空指定订单组的选中状态
   * @param orderId 订单组 ID
   */
  function clearOrderSelection(orderId: number) {
    cart.value[orderId].items.forEach((item) => {
      delete selectedItems.value[item.cartItemId];
    });
  }

  /**
   * 清空购物车数据（退出登录时调用）
   */
  function clearCart() {
    cart.value = getDefaultCartState();
    selectedItems.value = {};
    activeOrderId.value = 1;
    initialized.value = false;
  }

  // ============ 结算快照缓存相关方法 ============

  /**
   * 保存选中商品的完整数据快照到缓存（点击"去结算"时调用）
   * @description 创建商品数据的深拷贝快照，结算页面使用此快照数据，
   *              不会因为购物车数据变化而变化，除非重新点击"去结算"
   * @param orderId 订单组 ID
   */
  function saveCheckoutSnapshot(orderId: number) {
    const order = cart.value[orderId];
    if (!order) return;

    // 获取当前订单组中选中的商品完整数据（深拷贝）
    const selectedCartItems = order.items
      .filter(
        (item) =>
          selectedItems.value[item.cartItemId] &&
          !item.isInvalid &&
          item.isAvailable,
      )
      .map((item) => ({ ...item })); // 深拷贝商品数据

    const cache: CheckoutSnapshotCache = {
      orderId,
      orderName: order.name,
      items: selectedCartItems,
      timestamp: Date.now(),
    };

    // 存储到 localStorage
    const key = getCheckoutCacheKey(orderId);
    localStorage.setItem(key, JSON.stringify(cache));
  }

  /**
   * 获取结算快照数据
   * @param orderId 订单组 ID
   * @returns 快照数据，不存在则返回 null
   */
  function getCheckoutSnapshot(orderId: number): CheckoutSnapshotCache | null {
    const key = getCheckoutCacheKey(orderId);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    try {
      return JSON.parse(cached) as CheckoutSnapshotCache;
    } catch {
      return null;
    }
  }

  /**
   * 清除结算快照缓存（支付成功后调用）
   * @param orderId 订单组 ID
   */
  function clearCheckoutSnapshot(orderId: number) {
    const key = getCheckoutCacheKey(orderId);
    localStorage.removeItem(key);
  }

  // ============ 兼容旧方法名（保持向后兼容） ============

  /** @deprecated 请使用 saveCheckoutSnapshot */
  const saveCheckoutSelection = saveCheckoutSnapshot;

  /** @deprecated 请使用 getCheckoutSnapshot */
  const getCheckoutSelection = getCheckoutSnapshot;

  /** @deprecated 请使用 clearCheckoutSnapshot */
  const clearCheckoutSelection = clearCheckoutSnapshot;

  return {
    // Constants
    MAX_ITEMS_PER_ORDER,

    // State
    cart,
    activeOrderId,
    selectedItems,
    loading,
    initialized,

    // Getters
    currentOrder,
    totalCartCount,
    selectableItems,
    isAllSelected,
    selectedTotals,

    // Actions
    initCart,
    refreshCart,
    fetchOrderItems,
    addToCart,
    addToCartByOrders,
    updateQty,
    setQty,
    removeItem,
    toggleItemSelection,
    toggleAll,
    setActiveOrder,
    getOrderTotalItems,
    getSelectedItems,
    selectAllCurrentOrder,
    selectAllForOrder,
    clearOrderSelection,
    clearCart,

    // 订单限制相关
    checkOrderLimit,
    getOrderRemainingSlots,

    // 结算快照缓存相关
    saveCheckoutSnapshot,
    getCheckoutSnapshot,
    clearCheckoutSnapshot,

    // 兼容旧方法名
    saveCheckoutSelection,
    getCheckoutSelection,
    clearCheckoutSelection,
  };
});
